import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import { IoCheckmarkCircle, IoAlertCircle } from "react-icons/io5";
import Api from "../axiosConfig";

const Feedback = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [deliveryDetails, setDeliveryDetails] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: "",
  });

  useEffect(() => {
    const fetchDeliveryDetails = async () => {
      try {
        const response = await Api.get(`/api/v1/deliveries/track/${orderId}/details`);
        setDeliveryDetails(response.data);
      } catch (error) {
        console.error("Error fetching delivery details:", error);
      }
    };
    fetchDeliveryDetails();
  }, [orderId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setSubmitStatus({
        success: false,
        message: "Please select a rating before submitting",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await Api.post(`/api/v1/deliveries/track/${orderId}/feedback`, {
        rating,
        comment,
      });
      setSubmitStatus({
        success: true,
        message: "Thank you for your feedback!",
      });
      setTimeout(() => {
        navigate(`/track/${orderId}`);
      }, 2000);
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: "Failed to submit feedback. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-6">
          Delivery Feedback
        </h1>

        {/* Delivery Info */}
        {deliveryDetails && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-600">
              Order ID: <span className="font-medium">{orderId}</span>
            </p>
            <p className="text-gray-600">
              Status:{" "}
              <span className="font-medium">{deliveryDetails.status}</span>
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rating Stars */}
          <div className="flex flex-col space-y-2">
            <label className="text-gray-700 font-medium">
              Rate your delivery experience
            </label>
            <div className="flex space-x-1">
              {[...Array(5)].map((_, index) => {
                const ratingValue = index + 1;
                return (
                  <button
                    type="button"
                    key={ratingValue}
                    className={`text-2xl focus:outline-none transition-colors duration-200 ${
                      ratingValue <= (hover || rating)
                        ? "text-yellow-400"
                        : "text-gray-300"
                    }`}
                    onClick={() => setRating(ratingValue)}
                    onMouseEnter={() => setHover(ratingValue)}
                    onMouseLeave={() => setHover(0)}
                  >
                    <FaStar />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comment Box */}
          <div className="space-y-2">
            <label
              htmlFor="comment"
              className="text-gray-700 font-medium block"
            >
              Additional comments (optional)
            </label>
            <textarea
              id="comment"
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 border-solid rounded-md focus:outline-none"
              placeholder="Share your experience with this delivery..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          {/* Status Message */}
          {submitStatus.message && (
            <div
              className={`p-4 rounded-md ${
                submitStatus.success
                  ? "bg-green-50 text-green-800"
                  : "bg-red-50 text-red-800"
              }`}
            >
              <div className="flex items-center">
                {submitStatus.success ? (
                  <IoCheckmarkCircle className="h-5 w-5 mr-2" />
                ) : (
                  <IoAlertCircle className="h-5 w-5 mr-2" />
                )}
                {submitStatus.message}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              isSubmitting
                ? "bg-primary-DEFAULT cursor-not-allowed"
                : "bg-primary-dark hover:bg-primary-light"
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Feedback;
